import React from "react";

import { isStaleChunkError } from "../lib/isStaleChunkError";

/**
 * A lazy chunk that did not load.
 *
 * App's global `vite:preloadError` handler calls `preventDefault()` so it can
 * own the refresh UX (toast plus auto-reload). In a production build that
 * makes a failed `import()` resolve to `undefined` instead of rejecting, so
 * `React.lazy` would later throw an anonymous "cannot read properties of
 * undefined" TypeError. `requireLoadedModule` turns that case into this
 * typed error, which also lets `retryImport` see a rejection and retry.
 */
export class ChunkLoadError extends Error {
  constructor(chunk: string) {
    super(`The ${chunk} chunk failed to load.`);
    this.name = "ChunkLoadError";
  }
}

/** Use in a lazy factory: `import("./X").then((m) => requireLoadedModule(m, "X").X)`. */
export function requireLoadedModule<T>(module: T | undefined, chunk: string): T {
  if (module === undefined || module === null) throw new ChunkLoadError(chunk);
  return module;
}

function isChunkLoadFailure(error: unknown): boolean {
  return error instanceof ChunkLoadError || isStaleChunkError(error);
}

interface ChunkLoadBoundaryProps {
  /** Shown in the console message, e.g. "AutoSaver". */
  name: string;
  /** Rendered instead of the children after a chunk failure. Defaults to nothing. */
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

interface ChunkLoadBoundaryState {
  error: unknown;
}

/**
 * Contains the failure of an optional lazy chunk so it cannot replace the
 * whole app with the root error page. The global `vite:preloadError` handler
 * has already shown the "newer version, refresh" toast and scheduled a
 * reload, so this only logs and renders the fallback.
 *
 * Any other error (a real render bug inside the chunk) is rethrown to the
 * next boundary up, exactly as if this boundary were not there.
 */
export class ChunkLoadBoundary extends React.Component<
  ChunkLoadBoundaryProps,
  ChunkLoadBoundaryState
> {
  state: ChunkLoadBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): ChunkLoadBoundaryState {
    return { error };
  }

  componentDidCatch(error: unknown): void {
    if (isChunkLoadFailure(error)) {
      console.error(`[ChunkLoadBoundary] ${this.props.name} did not load`, error);
    }
  }

  render(): React.ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;
    if (!isChunkLoadFailure(error)) throw error;
    return this.props.fallback ?? null;
  }
}
